import { IdleTimeoutModal } from "@src/common/IdleTimeoutModal";
import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { ScheduleTimeOutModal } from "@src/common/ScheduleTimeOutModal";
import { APP_ROUTES, SESSION_STORAGE_KEYS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { IdleTimerProvider } from "@src/context/IdleTimerContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useClickLogging } from "@src/hooks/useClickLogging";
import { useMobileHeaderOffset } from "@src/hooks/useMobileHeaderOffset";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useAppNavigationBlocker } from "@src/hooks/useNavigationBlocker";
import { useNavigationLogging } from "@src/hooks/useNavigationLogging";
import { ENTRY_ROUTE_PATHS, isRegisteredEntryRoutePath } from "@src/routes/entry-route-registry";
import { isRequestInFlight } from "@src/services/request-manager";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Router } from "@uhg-abyss/web/ui/Router";
import { useEffect, useRef } from "react";
import { Footer } from "../Footer";
import { Header } from "../Header";

// Stryker disable ObjectLiteral,StringLiteral
const RootContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh !important",
  justifyContent: "space-between",
  overflow: "hidden",
});
// Stryker restore ObjectLiteral,StringLiteral

const TopContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  padding: 0,
  margin: 0,
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

const BottomContainer = styled("div", {});

export const AppBody = () => {
  const { data, setData, clearData, clearCompletedRoutes } = useApplicationContext();
  const { isUiBlocked, isBlockedNow, suppressGlobalOverlay } = useInteractionGuard();
  const { isInSalesforce } = useCanvasContext();
  const { showTimeoutModal, isTimerRunning, stopTimer } = useTimerContext();
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const navigate = useNavigateWithUTM();
  const location = useLocation();
  const pathname = location.pathname;
  const pendingBackRouteRef = useRef<string | null>(null);
  const mobileHeaderOffset = useMobileHeaderOffset({
    isMobile,
    isInSalesforce,
  });

  useAppNavigationBlocker(); // Use the custom hook
  useClickLogging(); // Use the click logging hook
  useNavigationLogging(); // Use the navigation logging hook

  // Block browser refresh (F5 / Ctrl-R) and tab close while a guarded action,
  // route transition, or managed API request is in progress. `isBlockedNow()`
  // and `isRequestInFlight()` are both synchronous, ref-backed reads — unlike
  // React state, they reflect the current lock state immediately, with no
  // render + effect cycle lag. That matters here because a guarded action can
  // resolve and synchronously trigger navigation (e.g. `location.href = ...`)
  // in the same microtask, before React would otherwise have flushed a
  // re-render; a state-derived check would still read stale (blocked) at that
  // point and incorrectly show the confirmation dialog.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isBlockedNow() || isRequestInFlight()) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    addEventListener("beforeunload", handleBeforeUnload);
    return () => removeEventListener("beforeunload", handleBeforeUnload);
  }, [isBlockedNow]);

  // On tab refresh from /patient-information, the unload handler sets this flag.
  // Redirect to /provider-profile so the user can re-select a time slot cleanly.
  // The flag is absent on tab close (sessionStorage is cleared by the browser),
  // so this effect is a no-op in that scenario.
  useEffect(() => {
    const redirectTo = sessionStorage.getItem(SESSION_STORAGE_KEYS.PATIENT_INFO_UNLOAD_REDIRECT);
    if (!redirectTo) {
      return;
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.PATIENT_INFO_UNLOAD_REDIRECT);
    navigate(redirectTo, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const shouldHandleConfirmedBack =
      sessionStorage.getItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK) === "true";
    if (!shouldHandleConfirmedBack || pathname === APP_ROUTES.APPOINTMENT_CONFIRMED) {
      return;
    }

    const storedRoute = sessionStorage.getItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK_ROUTE);
    const isReferrerFlow = data?.isReferrerFlow === true;

    let targetRoute: string;
    if (
      storedRoute &&
      ENTRY_ROUTE_PATHS.includes(storedRoute as (typeof ENTRY_ROUTE_PATHS)[number])
    ) {
      targetRoute = storedRoute;
    } else {
      targetRoute = isReferrerFlow ? APP_ROUTES.REFER : APP_ROUTES.GET_STARTED;
    }

    pendingBackRouteRef.current = targetRoute;

    const navigationEntry =
      typeof performance !== "undefined" && typeof performance.getEntriesByType === "function"
        ? (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)
        : undefined;
    const isBrowserRefresh = navigationEntry?.type === "reload";
    if (isBrowserRefresh) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK_RELOAD_ROUTE, targetRoute);
    }

    clearData();
    clearCompletedRoutes();
    if (targetRoute === APP_ROUTES.REFER) {
      setData("isReferrerFlow", true);
    }

    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK_ROUTE);
    navigate(targetRoute, { replace: true });
  }, [pathname, data?.isReferrerFlow, clearData, clearCompletedRoutes, setData, navigate]);

  useEffect(() => {
    if (pendingBackRouteRef.current) {
      if (pathname === pendingBackRouteRef.current) {
        const reloadRoute = sessionStorage.getItem(
          SESSION_STORAGE_KEYS.CONFIRMED_BACK_RELOAD_ROUTE,
        );
        if (reloadRoute === pathname && !data?.userUuid) {
          sessionStorage.removeItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK_RELOAD_ROUTE);
          // Stryker disable next-line MethodCall
          window.location.reload();
          return;
        }
        pendingBackRouteRef.current = null;
      }
      return;
    }

    if (!data || Object.keys(data).length === 0 || !data.userUuid) {
      // Stryker disable-line ConditionalExpression
      if (isTimerRunning) {
        stopTimer(); // Stop the timer if userUuid is missing
      }
      const isAllowedWithoutUserUuid =
        pathname === APP_ROUTES.ROOT ||
        isRegisteredEntryRoutePath(pathname) ||
        pathname === APP_ROUTES.APPOINTMENT_CONFIRMED;
      // Only navigate if the user is not already on an allowed entry route and there is no userUuid in context
      if (
        !isAllowedWithoutUserUuid &&
        !showTimeoutModal // Don't navigate if Timeoutmodal is open
      ) {
        navigate(APP_ROUTES.GET_STARTED);
      }
    }
  }, [data, pathname, navigate, showTimeoutModal, isTimerRunning, stopTimer]);

  return (
    <IdleTimerProvider>
      <LoadingOverlay
        isLoading={isUiBlocked && !suppressGlobalOverlay}
        ariaLoadingLabel="Processing..."
      />
      <RootContainer>
        {!isInSalesforce && (
          <TopContainer>
            <Header mobileHeaderOffset={mobileHeaderOffset} />
          </TopContainer>
        )}
        <div
          id="obc-app-body-content"
          data-testid="test-id-appbody-content"
          style={{
            flex: "auto",
            display: "flex",
            marginTop: isMobile ? mobileHeaderOffset : "0", // Match space to the rendered sticky header height on mobile
            justifyContent: "center",
          }}
        >
          <Router.Outlet />
        </div>
        {!isInSalesforce && (
          <BottomContainer>
            <Footer />
          </BottomContainer>
        )}
      </RootContainer>
      <ScheduleTimeOutModal />
      <IdleTimeoutModal />
    </IdleTimerProvider>
  );
};
